import React, { useState, useEffect, useRef } from 'react';

import { Modal, InputNumber, Typography, message, Tag, Space, Input, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useSerialNumberInput } from '@/hooks/useSerialNumberInput';

const { Text } = Typography;

const SerialNumbersContainer = styled.div`
  min-height: 100px;
  max-height: 250px;
  overflow-y: auto;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 12px;
`;

const SerialTag = styled(Tag)`
  font-size: 14px;
  padding: 6px 12px;
  margin: 4px;
  font-family: monospace;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CountInfo = styled.div<{ completed: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: ${props => props.completed ? 'rgba(34, 197, 94, 0.1)' : 'rgba(14, 116, 144, 0.1)'};
  border-radius: 6px;
  margin-top: 8px;
`;

const CountLabel = styled(Text)`
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
`;

const CountValue = styled(Text)<{ completed: boolean }>`
  font-size: 16px;
  font-weight: 700;
  color: ${props => props.completed ? '#22c55e' : '#0e7490'};
`;

interface BatchScanModalProduct {
  name?: string;
  sku?: string;
  barcode?: string | null;
  required_serial_numbers_count?: number;
  serial_number_pattern?: string | null;
  box_serial_number_delimiter?: string | null;
  [key: string]: unknown;
}

interface BatchScanModalItem {
  quantity: number;
  shipped_quantity?: number;
  product?: BatchScanModalProduct;
  [key: string]: unknown;
}

interface BatchScanModalProps {
  open: boolean;
  item: BatchScanModalItem | null;
  onConfirm: (quantity: number, serialNumbers?: string[]) => void;
  onCancel: () => void;
}

const BatchScanModal: React.FC<BatchScanModalProps> = ({ open, item, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState<number>(1);
  const serialListRef = useRef<HTMLDivElement | null>(null);

  const maxQuantity = item ? item.quantity - (item.shipped_quantity || 0) : 1;
  const requiredSerialNumbersCount = item?.product?.required_serial_numbers_count || 0;
  const needSerialNumber = requiredSerialNumbersCount > 0;
  const requiredSerialCount = quantity * requiredSerialNumbersCount;

  // 持有 auto-confirm 延遲計時器的 id，方便 modal 關閉 / unmount 時清掉，
  // 避免使用者在 300ms 窗口內按取消／關閉後 onConfirm 還是被觸發，
  // 對已 unmount 的 parent 做 stale state update。
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    input: currentInput,
    setInput: setCurrentInput,
    scannedSerials: serialNumbers,
    error: serialError,
    submit: submitSerial,
    removeAt: removeSerialAt,
    reset: resetSerials,
    replaceSerials,
    inputRef: serialInputRef,
    isCompleted: isSerialCompleted,
  } = useSerialNumberInput({
    productFlags: {
      required_serial_numbers_count: item?.product?.required_serial_numbers_count,
      serial_number_pattern: item?.product?.serial_number_pattern,
      box_serial_number_delimiter: item?.product?.box_serial_number_delimiter,
      product_barcode: item?.product?.barcode,
    },
    scanQuantity: quantity,
    onCompleted: (serials) => {
      // 延遲一下讓使用者看到序號被加入後再 auto-confirm。
      // 用 ref 持有 timer id，這樣 modal 在 300ms 內關閉 / unmount 時，
      // 下方的 useEffect 可以 clearTimeout 避免 onConfirm 還是被觸發。
      if (confirmTimerRef.current !== null) {
        clearTimeout(confirmTimerRef.current);
      }
      confirmTimerRef.current = setTimeout(() => {
        confirmTimerRef.current = null;
        onConfirm(quantity, serials);
      }, 300);
    },
  });

  const currentSerialCount = serialNumbers.length;

  // 當 Modal 打開時重置數量與序號。focus 由 useSerialNumberInput hook 在 mount 時
  // 處理 — operator 手持掃描槍時序號 input 直接 ready；若要先改 quantity 就點過去。
  useEffect(() => {
    if (open) {
      setQuantity(1);
      resetSerials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  // 序號變化時自動滾動到底部
  useEffect(() => {
    if (serialListRef.current && serialNumbers.length > 0) {
      serialListRef.current.scrollTop = serialListRef.current.scrollHeight;
    }
  }, [serialNumbers]);

  // 關閉 (open=false) 或 unmount 時清掉還沒觸發的 auto-confirm timer，
  // 避免在父層已經關閉 modal 後 onConfirm 還是被觸發、對 stale state 做 update。
  useEffect(() => {
    if (!open && confirmTimerRef.current !== null) {
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
    return () => {
      if (confirmTimerRef.current !== null) {
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
    };
  }, [open]);

  const handleQuantityChange = (value: number | null) => {
    const next = value as number;
    setQuantity(next);

    // 若降低 quantity 導致已掃序號超過新的需求量，截斷掉多的部分，
    // 避免使用者改完件數後還卡著超量序號。
    if (needSerialNumber && serialNumbers.length > 0) {
      const newRequiredCount = next * requiredSerialNumbersCount;
      if (serialNumbers.length > newRequiredCount) {
        replaceSerials(serialNumbers.slice(0, newRequiredCount));
      }
    }
  };

  const handleQuantityKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (needSerialNumber) {
        // 如果需要序號，focus 到序號輸入框
        serialInputRef.current?.focus();
      } else {
        // 否則直接確認
        handleConfirm();
      }
    }
  };

  const handleConfirm = () => {

    if (quantity <= 0) {
      message.error(t('operator.qc.batch_scan_modal.invalid_quantity', { defaultValue: '請輸入有效數量' }) as string);
      return;
    }

    if (quantity > maxQuantity) {
      message.error(t('operator.qc.batch_scan_modal.quantity_exceeded', {
        defaultValue: '輸入數量超過剩餘數量',
        max: maxQuantity
      }) as string);
      return;
    }

    // 如果需要序號，驗證序號數量
    if (needSerialNumber) {

      if (serialNumbers.length !== requiredSerialCount) {
        message.error(t('operator.qc.batch_scan_modal.serial_count_mismatch', {
          defaultValue: '序號數量不符，需要 {{required}} 個序號（目前 {{actual}} 個）',
          required: requiredSerialCount,
          actual: serialNumbers.length
        }) as string);
        return;
      }

      onConfirm(quantity, serialNumbers);
    } else {
      onConfirm(quantity);
    }
  };

  return (
    <Modal
      title={t('operator.qc.batch_scan_modal.title', { defaultValue: '批次刷驗' })}
      open={open}
      onOk={handleConfirm}
      onCancel={onCancel}
      okText={t('common.button.confirm', { defaultValue: '確認' })}
      cancelText={t('common.button.cancel', { defaultValue: '取消' })}
      width={needSerialNumber ? 560 : 480}
      centered
      destroyOnHidden
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {item && (
          <div>
            <Text strong style={{ fontSize: '16px' }}>
              {item.product?.name || '--'}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {t('operator.qc.batch_scan_modal.sku', { defaultValue: 'SKU' })}: {item.product?.sku || '--'}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: '14px' }}>
              {t('operator.qc.batch_scan_modal.remaining', { defaultValue: '剩餘數量' })}: {maxQuantity}
            </Text>
          </div>
        )}

        <div>
          <Text style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            {t('operator.qc.batch_scan_modal.quantity_label', { defaultValue: '刷驗數量' })}
          </Text>
          <InputNumber
            value={quantity}
            onChange={handleQuantityChange}
            min={1}
            max={maxQuantity}
            style={{ width: '100%' }}
            size="large"
            onKeyDown={handleQuantityKeyDown}
          />
        </div>

        {needSerialNumber && (
          <div>
            <Text style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
              {t('operator.qc.batch_scan_modal.scanned_serials', { defaultValue: '已掃描序號' })}
            </Text>

            <CountInfo completed={isSerialCompleted}>
              <CountLabel>
                {t('operator.qc.batch_scan_modal.serial_count_label', { defaultValue: '已掃描序號數量' })}
              </CountLabel>
              <CountValue completed={isSerialCompleted}>
                {currentSerialCount} / {requiredSerialCount}
              </CountValue>
            </CountInfo>

            {serialError && (
              <Alert
                type="error"
                message={serialError}
                showIcon
                style={{ marginBottom: 8 }}
              />
            )}

            <SerialNumbersContainer ref={serialListRef}>
              {serialNumbers.length === 0 ? (
                <Text type="secondary" style={{ fontSize: '14px' }}>
                  {t('operator.qc.batch_scan_modal.no_serials', { defaultValue: '尚未掃描序號，請在下方輸入框掃描' })}
                </Text>
              ) : (
                <Space size={[8, 8]} wrap>
                  {serialNumbers.map((serialNumber, index) => (
                    <SerialTag
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        removeSerialAt(index);
                        serialInputRef.current?.focus();
                      }}
                      color="blue"
                    >
                      {serialNumber}
                    </SerialTag>
                  ))}
                </Space>
              )}
            </SerialNumbersContainer>

            <InputWrapper>
              <Text style={{ fontWeight: 500 }}>
                {t('operator.qc.batch_scan_modal.input_label', { defaultValue: '掃描序號' })}
              </Text>
              <Input
                ref={serialInputRef}
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                onPressEnter={() => submitSerial()}
                placeholder={t('operator.qc.batch_scan_modal.input_placeholder', {
                  defaultValue: '請掃描或輸入序號後按 Enter'
                })}
                size="large"
                style={{ fontFamily: 'monospace' }}
                disabled={isSerialCompleted}
              />
              {isSerialCompleted && (
                <Text type="success" style={{ fontSize: '12px' }}>
                  {t('operator.qc.batch_scan_modal.completed', { defaultValue: '✓ 已完成所需序號掃描' })}
                </Text>
              )}
            </InputWrapper>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default BatchScanModal;
